import { api, API_BASE, ApiError, refreshAccessToken } from './apiClient'
import { useAuthStore } from '@/stores/authStore'
import type { KnowledgeGraph } from '@/types/api'

export interface KnowledgeBaseDto {
  id?: string
  kb_id?: string
  knowledge_base_id?: string
  name?: string
  display_name?: string
  title?: string
  description?: string
  status?: 'active' | 'archived' | 'trash' | string
  is_archived?: boolean
  is_deleted?: boolean
  /** Real backend lifecycle flag (PATCH body/response). */
  deleted?: boolean
  file_count?: number
  files_count?: number
  document_count?: number
  created_at?: string
  updated_at?: string
  tags?: string[]
  /** When false, KB is draft until published. Omitted/true = live. */
  published?: boolean
  /** Set when a release is published to consumers — list shows Live. */
  published_release_id?: string | null
  etag?: string
}

export interface KnowledgeBaseFileDto {
  id?: string
  name?: string
  path: string
  size?: number
  size_bytes?: number
  format?: string
  content_type?: string
  mime_type?: string
  updated_at?: string
  created_at?: string
  sha256?: string
  /** List view: unpublished edits exist for this path. */
  has_draft?: boolean
  draft_pending?: boolean
  status?: string
  /** Staging file list: published content is live when true. */
  is_live?: boolean
  /** When `is_live`, GET …/versions/{live_version_id} for preview. */
  live_version_id?: string
}

export function knowledgeBaseDtoIsLive(dto?: KnowledgeBaseDto | null): boolean {
  return Boolean(dto?.published_release_id?.trim())
}

/** Draft badge on file list rows from list/detail DTO hints. */
export function knowledgeBaseFileShowsDraftBadge(
  file: KnowledgeBaseFileDto,
): boolean {
  if (file.has_draft === true || file.draft_pending === true) return true
  const status = file.status?.toLowerCase()
  return status === 'draft' || status === 'drafted'
}

/** Files tab Live vs Draft from list `is_live` (staging). */
export function knowledgeBaseFileIsLive(file: KnowledgeBaseFileDto): boolean {
  return file.is_live === true
}

export interface KnowledgeBaseChangeDto {
  path?: string
  change_type?: string
  updated_at?: string
  [key: string]: unknown
}

export type KnowledgeBaseChangeKind =
  'added' | 'deleted' | 'metadata' | 'renamed' | 'modified'

export interface KnowledgeBaseChangeTableRow {
  path: string
  kind: KnowledgeBaseChangeKind
}

export interface KnowledgeBaseChangesWarning {
  path?: string
  field?: string | null
  message?: string
}

/** Staging `GET …/changes` envelope (KB detail Changes tab). */
export interface KnowledgeBaseChangesDetail {
  kb_id?: string
  published_release_id?: string | null
  published_release_num?: number
  has_changes?: boolean
  draft_sha256?: string
  changes: KnowledgeBaseReleasePathChanges
  problems?: unknown[]
  warnings?: KnowledgeBaseChangesWarning[]
  publishable?: boolean
  published_operation?: string
  published_source_release_id?: string | null
}

const KNOWLEDGE_BASE_CHANGE_TABLE_KINDS: KnowledgeBaseChangeKind[] = [
  'added',
  'deleted',
  'metadata',
  'renamed',
  'modified',
]

export function knowledgeBaseChangeKindLabel(
  kind: KnowledgeBaseChangeKind,
): string {
  return kind.charAt(0).toUpperCase() + kind.slice(1)
}

export function knowledgeBaseChangesTableRows(
  changes: KnowledgeBaseReleasePathChanges | undefined,
): KnowledgeBaseChangeTableRow[] {
  if (!changes) return []
  const rows: KnowledgeBaseChangeTableRow[] = []
  for (const kind of KNOWLEDGE_BASE_CHANGE_TABLE_KINDS) {
    for (const path of changes[kind] ?? []) {
      if (typeof path === 'string' && path.trim()) {
        rows.push({ path: path.trim(), kind })
      }
    }
  }
  return rows.sort((a, b) => a.path.localeCompare(b.path))
}

function parseKnowledgeBaseChangesDetail(
  res: unknown,
): KnowledgeBaseChangesDetail {
  const record =
    res && typeof res === 'object' && !Array.isArray(res)
      ? (res as Record<string, unknown>)
      : null
  const changesRaw = record?.changes
  let pathChanges: KnowledgeBaseReleasePathChanges = {}
  if (
    changesRaw &&
    typeof changesRaw === 'object' &&
    !Array.isArray(changesRaw)
  ) {
    pathChanges = changesRaw as KnowledgeBaseReleasePathChanges
  } else if (Array.isArray(changesRaw)) {
    for (const row of changesRaw) {
      if (!row || typeof row !== 'object') continue
      const item = row as KnowledgeBaseChangeDto
      const path = item.path?.trim()
      if (!path) continue
      const kindRaw = String(item.change_type ?? 'modified').toLowerCase()
      const kind: KnowledgeBaseChangeKind =
        kindRaw === 'added' ||
        kindRaw === 'deleted' ||
        kindRaw === 'metadata' ||
        kindRaw === 'renamed'
          ? kindRaw
          : 'modified'
      const list = pathChanges[kind] ?? []
      list.push(path.startsWith('/') ? path : `/${path}`)
      pathChanges[kind] = list
    }
  }
  const warningsRaw = record?.warnings
  const warnings = Array.isArray(warningsRaw)
    ? warningsRaw.filter(
        (w): w is KnowledgeBaseChangesWarning =>
          w != null && typeof w === 'object',
      )
    : undefined
  return {
    kb_id: typeof record?.kb_id === 'string' ? record.kb_id : undefined,
    published_release_id:
      typeof record?.published_release_id === 'string'
        ? record.published_release_id
        : record?.published_release_id === null
          ? null
          : undefined,
    published_release_num:
      typeof record?.published_release_num === 'number'
        ? record.published_release_num
        : undefined,
    has_changes: record?.has_changes === true,
    draft_sha256:
      typeof record?.draft_sha256 === 'string'
        ? record.draft_sha256
        : undefined,
    changes: pathChanges,
    problems: Array.isArray(record?.problems) ? record.problems : undefined,
    warnings,
    publishable: record?.publishable !== false,
    published_operation:
      typeof record?.published_operation === 'string'
        ? record.published_operation
        : undefined,
    published_source_release_id:
      typeof record?.published_source_release_id === 'string'
        ? record.published_source_release_id
        : record?.published_source_release_id === null
          ? null
          : undefined,
  }
}

export interface KnowledgeBaseReleasePathChanges {
  added?: string[]
  modified?: string[]
  deleted?: string[]
  renamed?: string[]
  metadata?: string[]
}

export interface KnowledgeBaseReleaseDto {
  release_id?: string
  kb_id?: string
  release_num?: number
  operation?: string
  source_release_id?: string | null
  description?: string
  default_instructions?: string | null
  note?: string | null
  changes?: KnowledgeBaseReleasePathChanges
  file_count?: number
  total_bytes?: number
  published_by_personal_id?: number
  is_published?: boolean
  created_at?: string
  /** Legacy mock fields */
  id?: string
  label?: string
  is_active?: boolean
  [key: string]: unknown
}

export interface KnowledgeBaseReleasesPage {
  kb_id?: string
  releases: KnowledgeBaseReleaseDto[]
  next_cursor?: string | null
}

export function knowledgeBaseReleaseIsLive(
  dto: KnowledgeBaseReleaseDto,
): boolean {
  return dto.is_published === true || dto.is_active === true
}

/** Releases tab: Rollback only on unpublished releases (`is_published === false`). */
export function knowledgeBaseReleaseShowsRollbackAction(
  dto: KnowledgeBaseReleaseDto,
): boolean {
  return dto.is_published === false
}

/** Path segment for `POST …/releases/{RELEASE_ID}/rollback`. */
export function knowledgeBaseReleaseWireId(
  dto: KnowledgeBaseReleaseDto,
): string | null {
  const id = dto.release_id?.trim() || dto.id?.trim()
  return id || null
}

export function knowledgeBaseReleaseTitle(
  dto: KnowledgeBaseReleaseDto,
): string {
  if (dto.label?.trim()) return dto.label.trim()
  if (dto.release_num != null) return `Release ${dto.release_num}`
  return dto.release_id ?? dto.id ?? 'Release'
}

export function knowledgeBaseReleaseMeta(dto: KnowledgeBaseReleaseDto): string {
  const parts: string[] = []
  if (dto.operation?.trim()) {
    parts.push(humanizeEventKind(dto.operation.trim()))
  }
  if (dto.file_count != null) {
    const n = dto.file_count
    parts.push(`${n} file${n === 1 ? '' : 's'}`)
  }
  if (dto.published_by_personal_id != null) {
    parts.push(`User ${dto.published_by_personal_id}`)
  }
  return parts.join(' · ')
}

export function knowledgeBaseReleaseChangeSummary(
  dto: KnowledgeBaseReleaseDto,
): string | null {
  const c = dto.changes
  if (!c) return null
  const stats: string[] = []
  if (c.added?.length) stats.push(`${c.added.length} added`)
  if (c.modified?.length) stats.push(`${c.modified.length} modified`)
  if (c.deleted?.length) stats.push(`${c.deleted.length} deleted`)
  if (c.renamed?.length) stats.push(`${c.renamed.length} renamed`)
  if (c.metadata?.length) stats.push(`${c.metadata.length} metadata`)
  if (stats.length === 0) return null
  const paths = c.added?.length ? c.added.slice(0, 2).join(', ') : undefined
  if (paths && (c.added?.length ?? 0) <= 2) {
    return `${stats.join(', ')} · ${paths}`
  }
  return stats.join(', ')
}

export interface KnowledgeBaseEventChangeCounts {
  added?: number
  deleted?: number
  renamed?: number
  metadata?: number
  modified?: number
}

export interface KnowledgeBasePublishedEventDetail {
  paths?: string[] | null
  changes?: KnowledgeBaseEventChangeCounts
  release_num?: number
}

export interface KnowledgeBaseCreatedEventDetail {
  name?: string
}

export interface KnowledgeBaseEventDto {
  event_id?: string
  /** Staging wire field (e.g. `published`, `archived`, `created`). */
  event?: string
  /** Legacy mock / alias */
  event_type?: string
  personal_id?: number
  detail?:
    | KnowledgeBasePublishedEventDetail
    | KnowledgeBaseCreatedEventDetail
    | Record<string, unknown>
    | null
  created_at?: string
  actor?: string
  summary?: string
  [key: string]: unknown
}

export interface KnowledgeBaseEventsPage {
  kb_id?: string
  events: KnowledgeBaseEventDto[]
  next_cursor?: string | null
}

function humanizeEventKind(kind: string): string {
  const normalized = kind.trim().replace(/_/g, ' ')
  if (!normalized) return 'Activity'
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

function formatPublishedEventDetail(
  detail: KnowledgeBasePublishedEventDetail,
): string {
  const parts: string[] = []
  if (detail.release_num != null) {
    parts.push(`Release ${detail.release_num}`)
  }
  const c = detail.changes
  if (c) {
    const stats: string[] = []
    if (c.added) stats.push(`${c.added} added`)
    if (c.modified) stats.push(`${c.modified} modified`)
    if (c.deleted) stats.push(`${c.deleted} deleted`)
    if (c.renamed) stats.push(`${c.renamed} renamed`)
    if (c.metadata) stats.push(`${c.metadata} metadata`)
    if (stats.length > 0) parts.push(stats.join(', '))
  }
  return parts.join(' · ')
}

export function knowledgeBaseEventKind(dto: KnowledgeBaseEventDto): string {
  return (dto.event ?? dto.event_type ?? '').trim()
}

/** Primary line for Events tab rows (matches staging `event` + `detail`). */
export function knowledgeBaseEventTitle(dto: KnowledgeBaseEventDto): string {
  if (dto.summary) return dto.summary
  const kind = knowledgeBaseEventKind(dto)
  const detail = dto.detail
  if (kind === 'created' && detail && typeof detail === 'object') {
    const name = (detail as KnowledgeBaseCreatedEventDetail).name
    if (typeof name === 'string' && name.trim()) {
      return `Created · ${name.trim()}`
    }
  }
  if (kind === 'published' && detail && typeof detail === 'object') {
    const line = formatPublishedEventDetail(
      detail as KnowledgeBasePublishedEventDetail,
    )
    return line ? `Published · ${line}` : 'Published'
  }
  if (kind) return humanizeEventKind(kind)
  return 'Activity'
}

export function knowledgeBaseEventMeta(
  dto: KnowledgeBaseEventDto,
): string | null {
  if (dto.actor?.trim()) return dto.actor.trim()
  if (dto.personal_id != null) return `User ${dto.personal_id}`
  return null
}

export type KnowledgeBaseAuthoringSchemaDto = {
  schema?: string
  kinds?: string[]
  lifecycles?: string[]
  scopes?: string[]
  cardinalities?: string[]
  fields?: string[]
  relation_fields?: string[]
  required?: {
    all?: string[]
    entity?: string[]
  }
}

export type KnowledgeBaseAuthoringTableColumnDto = {
  name: string
  type: string
}

export type KnowledgeBaseAuthoringTableJoinDto = {
  table: string
  direction: string
  join_key: string
}

export type KnowledgeBaseAuthoringTableDto = {
  name: string
  description?: string
  columns?: KnowledgeBaseAuthoringTableColumnDto[]
  joins?: KnowledgeBaseAuthoringTableJoinDto[]
}

export type KnowledgeBaseAuthoringOptions = {
  schema?: KnowledgeBaseAuthoringSchemaDto
  tables?: KnowledgeBaseAuthoringTableDto[]
  entities?: string[]
  categories?: string[]
  verbs?: string[]
}

export const KB_AUTHORING_FALLBACK_LIFECYCLES = [
  'master-data',
  'transactional',
  'analytical',
  'reference',
] as const

export const KB_AUTHORING_FALLBACK_SCOPES = [
  'tenant-org',
  'tenant',
  'global',
] as const

export const KB_AUTHORING_FALLBACK_CARDINALITIES = [
  'N:1',
  '1:N',
  '1:1',
  'M:N',
] as const

/** Normalized prefill for Add file dialog (GET …/authoring_options). */
export type KnowledgeBaseAddFilePrefill = {
  kind?: 'entity' | 'guide'
  title?: string
  name?: string
  description?: string
  body?: string
  category?: string
  lifecycle?: string
  scope?: string
  sourceTables?: string
  relations?: Array<{
    operates: string
    liftTruck: string
    cardinality: string
    description: string
    fromTables: string
    toTables: string
    via: string
  }>
}

export type KnowledgeBaseAuthoringOptionsResolved = {
  schemaVersion: string | null
  lifecycles: string[]
  scopes: string[]
  cardinalities: string[]
  dialogKinds: Array<'entity' | 'guide'>
  tableNames: string[]
  tables: KnowledgeBaseAuthoringTableDto[]
  joinKeys: string[]
  verbs: string[]
  categories: string[]
  entities: string[]
  requiredAll: string[]
  requiredEntity: string[]
  formPrefill: KnowledgeBaseAddFilePrefill
}

/** Unique `join_key` values from all authoring catalog tables, stable sort. */
export function collectKnowledgeBaseAuthoringJoinKeys(
  tables: KnowledgeBaseAuthoringTableDto[],
): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const table of tables) {
    for (const join of table.joins ?? []) {
      const key = join.join_key.trim()
      if (!key || seen.has(key)) continue
      seen.add(key)
      out.push(key)
    }
  }
  return out.sort((a, b) => a.localeCompare(b))
}

function readAuthoringString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

function readAuthoringStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean)
  }
  if (typeof value === 'string') {
    return value
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean)
  }
  return []
}

function readAuthoringKind(value: unknown): 'entity' | 'guide' | undefined {
  if (value === 'entity' || value === 'guide') return value
  return undefined
}

function readAuthoringBlock(
  value: unknown,
): Record<string, unknown> | undefined {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return undefined
}

type KnowledgeBaseAddFilePrefillRelation = NonNullable<
  KnowledgeBaseAddFilePrefill['relations']
>[number]

function mapAuthoringRelation(
  raw: unknown,
  defaultCardinality: string,
): KnowledgeBaseAddFilePrefillRelation {
  const o = readAuthoringBlock(raw)
  if (!o) {
    return {
      operates: '',
      liftTruck: '',
      cardinality: defaultCardinality,
      description: '',
      fromTables: '',
      toTables: '',
      via: '',
    }
  }
  const fromList = readAuthoringStringList(o.from_tables ?? o.fromTables)
  const toList = readAuthoringStringList(o.to_tables ?? o.toTables)
  return {
    operates:
      readAuthoringString(
        o.verb ?? o.predicate ?? o.operates ?? o.predicate_name,
      ) ?? '',
    liftTruck:
      readAuthoringString(o.to ?? o.object ?? o.liftTruck ?? o.lift_truck) ??
      '',
    cardinality: readAuthoringString(o.cardinality) ?? defaultCardinality,
    description: readAuthoringString(o.meaning ?? o.description) ?? '',
    fromTables: fromList.join(', '),
    toTables: toList.join(', '),
    via: readAuthoringString(o.join_key ?? o.join_notes ?? o.via) ?? '',
  }
}

function readAuthoringStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean)
}

function parseAuthoringTables(raw: unknown): KnowledgeBaseAuthoringTableDto[] {
  if (!Array.isArray(raw)) return []
  return raw.flatMap((item) => {
    const row = readAuthoringBlock(item)
    if (!row) return []
    const name = readAuthoringString(row.name)
    if (!name) return []
    const columns = Array.isArray(row.columns)
      ? row.columns.flatMap((col) => {
          const c = readAuthoringBlock(col)
          if (!c) return []
          const colName = readAuthoringString(c.name)
          const colType = readAuthoringString(c.type)
          if (!colName || !colType) return []
          return [{ name: colName, type: colType }]
        })
      : undefined
    const joins = Array.isArray(row.joins)
      ? row.joins.flatMap((join) => {
          const j = readAuthoringBlock(join)
          if (!j) return []
          const table = readAuthoringString(j.table)
          const direction = readAuthoringString(j.direction)
          const join_key = readAuthoringString(j.join_key)
          if (!table || !direction || !join_key) return []
          return [{ table, direction, join_key }]
        })
      : undefined
    return [
      {
        name,
        description: readAuthoringString(row.description),
        columns,
        joins,
      },
    ]
  })
}

/** Normalize raw GET …/authoring_options JSON. */
export function normalizeKnowledgeBaseAuthoringOptions(
  raw: unknown,
): KnowledgeBaseAuthoringOptions {
  const root = readAuthoringBlock(raw) ?? {}
  const schemaBlock = readAuthoringBlock(root.schema) ?? {}
  const schema: KnowledgeBaseAuthoringSchemaDto = {
    schema: readAuthoringString(schemaBlock.schema ?? root.schema),
    kinds: readAuthoringStringArray(schemaBlock.kinds),
    lifecycles: readAuthoringStringArray(schemaBlock.lifecycles),
    scopes: readAuthoringStringArray(schemaBlock.scopes),
    cardinalities: readAuthoringStringArray(schemaBlock.cardinalities),
    fields: readAuthoringStringArray(schemaBlock.fields),
    relation_fields: readAuthoringStringArray(schemaBlock.relation_fields),
    required: readAuthoringBlock(schemaBlock.required)
      ? {
          all: readAuthoringStringArray(
            (readAuthoringBlock(schemaBlock.required) ?? {}).all,
          ),
          entity: readAuthoringStringArray(
            (readAuthoringBlock(schemaBlock.required) ?? {}).entity,
          ),
        }
      : undefined,
  }
  return {
    schema,
    tables: parseAuthoringTables(root.tables),
    entities: readAuthoringStringArray(root.entities),
    categories: readAuthoringStringArray(root.categories),
    verbs: readAuthoringStringArray(root.verbs),
  }
}

function dialogKindsFromSchema(kinds: string[]): Array<'entity' | 'guide'> {
  const out: Array<'entity' | 'guide'> = []
  if (kinds.includes('entity')) out.push('entity')
  if (kinds.includes('guide')) out.push('guide')
  if (out.length === 0) return ['entity', 'guide']
  return out
}

/** Resolve catalog + form defaults for the Add file dialog. */
export function resolveKnowledgeBaseAuthoringOptions(
  options: KnowledgeBaseAuthoringOptions,
  raw?: unknown,
): KnowledgeBaseAuthoringOptionsResolved {
  const schema = options.schema ?? {}
  const lifecycles =
    schema.lifecycles && schema.lifecycles.length > 0
      ? schema.lifecycles
      : [...KB_AUTHORING_FALLBACK_LIFECYCLES]
  const scopes =
    schema.scopes && schema.scopes.length > 0
      ? schema.scopes
      : [...KB_AUTHORING_FALLBACK_SCOPES]
  const cardinalities =
    schema.cardinalities && schema.cardinalities.length > 0
      ? schema.cardinalities
      : [...KB_AUTHORING_FALLBACK_CARDINALITIES]
  const dialogKinds = dialogKindsFromSchema(schema.kinds ?? [])
  const tables = options.tables ?? []
  const tableNames = tables.map((t) => t.name)
  const joinKeys = collectKnowledgeBaseAuthoringJoinKeys(tables)
  const verbs = options.verbs ?? []
  const categories = options.categories ?? []
  const entities = options.entities ?? []
  const defaultCardinality = cardinalities[0] ?? 'N:1'

  const legacyPrefill = parseLegacyAuthoringPrefill(
    readAuthoringBlock(raw) ?? (options as Record<string, unknown>),
    defaultCardinality,
  )

  const formPrefill: KnowledgeBaseAddFilePrefill = {
    kind: legacyPrefill.kind ?? dialogKinds[0],
    lifecycle: legacyPrefill.lifecycle ?? lifecycles[0],
    scope: legacyPrefill.scope ?? scopes[0],
    category: legacyPrefill.category ?? categories[0] ?? '',
    title: legacyPrefill.title,
    name: legacyPrefill.name,
    description: legacyPrefill.description,
    body: legacyPrefill.body,
    sourceTables: legacyPrefill.sourceTables,
    relations: legacyPrefill.relations ?? [
      {
        operates: verbs[0] ?? '',
        liftTruck: entities[0] ?? '',
        cardinality: defaultCardinality,
        description: '',
        fromTables: '',
        toTables: '',
        via: '',
      },
    ],
  }

  return {
    schemaVersion: schema.schema ?? null,
    lifecycles,
    scopes,
    cardinalities,
    dialogKinds,
    tableNames,
    tables,
    joinKeys,
    verbs,
    categories,
    entities,
    requiredAll: schema.required?.all ?? [],
    requiredEntity: schema.required?.entity ?? [],
    formPrefill,
  }
}

function parseLegacyAuthoringPrefill(
  root: Record<string, unknown>,
  defaultCardinality: string,
): KnowledgeBaseAddFilePrefill {
  const kind =
    readAuthoringKind(root.default_kind ?? root.kind) ??
    (root.guide && !root.entity ? 'guide' : undefined)
  const entity = readAuthoringBlock(root.entity) ?? root
  const guide = readAuthoringBlock(root.guide)
  const block = kind === 'guide' ? (guide ?? entity) : (entity ?? guide ?? root)
  const sourceList = readAuthoringStringList(
    block.source_tables ?? block.sourceTables ?? root.source_tables,
  )
  const relationRaw =
    block.relations ??
    block.default_relations ??
    block.relation_templates ??
    root.relations ??
    root.default_relations
  const relations = Array.isArray(relationRaw)
    ? relationRaw.map((r) => mapAuthoringRelation(r, defaultCardinality))
    : relationRaw
      ? [mapAuthoringRelation(relationRaw, defaultCardinality)]
      : undefined

  return {
    kind,
    title: readAuthoringString(block.title ?? root.title),
    name: readAuthoringString(block.name ?? root.name),
    description: readAuthoringString(block.description ?? root.description),
    body: readAuthoringString(block.body ?? root.body),
    category: readAuthoringString(block.category ?? root.category),
    lifecycle: readAuthoringString(block.lifecycle ?? root.lifecycle),
    scope: readAuthoringString(block.scope ?? root.scope),
    sourceTables:
      sourceList.length > 0
        ? sourceList.join('\n')
        : readAuthoringString(block.source_tables ?? block.sourceTables),
    relations,
  }
}

/** @deprecated Prefer resolveKnowledgeBaseAuthoringOptions().formPrefill */
export function parseKnowledgeBaseAuthoringOptionsPrefill(
  options: KnowledgeBaseAuthoringOptions | Record<string, unknown>,
): KnowledgeBaseAddFilePrefill {
  const normalized = normalizeKnowledgeBaseAuthoringOptions(options)
  return resolveKnowledgeBaseAuthoringOptions(normalized, options).formPrefill
}

function parseListEnvelope<T>(res: unknown, keys: string[]): T[] {
  if (Array.isArray(res)) return res as T[]
  if (res && typeof res === 'object') {
    const record = res as Record<string, unknown>
    for (const key of keys) {
      const val = record[key]
      if (Array.isArray(val)) return val as T[]
    }
  }
  return []
}

export interface FileContentResult {
  path: string
  /** Published / live body (`content` on the wire). */
  content: string
  /** Unpublished draft body when the API returns `draft_content`. */
  draft_content?: string
  contentType: string
  size?: number
  /** ETag for optimistic concurrency on PUT (If-Match). */
  sha256?: string
  /** Head / draft version row id from GET file (`version_id` on the wire). */
  version_id?: string
  version_num?: number
}

/** PUT …/files/{path} response (staging WriteFileResponse). */
export interface WriteFileSaveResult {
  path?: string
  sha256?: string
  size_bytes?: number
  version_num?: number
  /** Staging PUT: true when the file row was created (skip follow-up publish). */
  created?: boolean
}

/** Staging PUT …/files/{path} body (authoring); OpenAPI still documents `{ content }` only. */
export type KnowledgeBaseFileWriteRequest = {
  doc: Record<string, unknown>
  body: string
}

export type KnowledgeBaseFileDraftSavePayload =
  string | KnowledgeBaseFileWriteRequest

function knowledgeBaseFileDraftPutBody(
  payload: KnowledgeBaseFileDraftSavePayload,
): { content: string } | KnowledgeBaseFileWriteRequest {
  if (typeof payload === 'string') return { content: payload }
  return payload
}

export function normalizeKnowledgeBaseFilePath(filePath: string): string {
  return filePath.replace(/^\/+/, '')
}

function parseWriteFileSaveResponse(res: unknown): WriteFileSaveResult {
  if (!res || typeof res !== 'object' || Array.isArray(res)) return {}
  const r = res as Record<string, unknown>
  const versionRaw = r.version_num
  return {
    path: typeof r.path === 'string' ? r.path : undefined,
    sha256: typeof r.sha256 === 'string' ? r.sha256 : undefined,
    size_bytes: typeof r.size_bytes === 'number' ? r.size_bytes : undefined,
    version_num:
      typeof versionRaw === 'number' && Number.isFinite(versionRaw)
        ? versionRaw
        : undefined,
    created: r.created === true,
  }
}

/** Add file dialog: PUT `{ doc, body }`, then GET …/changes for the new path. */
export async function createKnowledgeBaseFileFromAuthoring(
  kbId: string,
  filePath: string,
  write: KnowledgeBaseFileWriteRequest,
): Promise<WriteFileSaveResult> {
  const saved = await saveKnowledgeBaseFileDraft(kbId, filePath, write)
  const scopePath = normalizeKnowledgeBaseFilePath(filePath)
  try {
    await fetchKnowledgeBaseChangesDetail(kbId, scopePath)
  } catch {
    // PUT succeeded; changes refresh is best-effort for tab counts / publish prep.
  }
  return saved
}

export interface KnowledgeBasePublishOptions {
  /** When set, `GET …/changes?paths=` runs before publish (file-scoped). */
  filePath?: string
  /** Publish multiple paths in one POST (Changes tab **Publish all**). */
  paths?: string[]
  /**
   * Fallback `If-Match` only when `GET …/changes` omits `draft_sha256`.
   * Never the file-content `sha256` from PUT …/files/{path}.
   */
  draft_sha256?: string
}

export type KnowledgeBaseChangesSnapshot = KnowledgeBaseChangesDetail & {
  /** Value for `If-Match` on POST …/publish. */
  if_match?: string
}

/** GET …/changes returned `publishable: false` — do not call POST …/publish. */
export class KnowledgeBaseNotPublishableError extends Error {
  constructor() {
    super('Could not publish document')
    this.name = 'KnowledgeBaseNotPublishableError'
  }
}

export function isKnowledgeBaseNotPublishableError(
  err: unknown,
): err is KnowledgeBaseNotPublishableError {
  return err instanceof KnowledgeBaseNotPublishableError
}

function knowledgeBaseChangesIfMatch(
  etagHeader: string | null | undefined,
  draft_sha256?: string,
): string | undefined {
  const fromHeader = etagHeader?.trim()
  if (fromHeader) {
    return fromHeader.startsWith('"') ? fromHeader : `"${fromHeader}"`
  }
  const hash = draft_sha256?.trim().replace(/^"|"$/g, '')
  return hash ? `"${hash}"` : undefined
}

async function getKnowledgeBaseChangesJson(
  path: string,
): Promise<{ json: unknown; etag: string | null }> {
  const exec = async (): Promise<Response> => {
    const token = useAuthStore.getState().accessToken
    const headers = new Headers()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    return fetch(`${API_BASE}${path}`, {
      headers,
      credentials: 'same-origin',
    })
  }
  let res = await exec()
  if (res.status === 401 && useAuthStore.getState().accessToken) {
    const token = await refreshAccessToken()
    if (token === null) throw new ApiError(401, null)
    res = await exec()
  }
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body)
  }
  const json = await res.json()
  return { json, etag: res.headers.get('ETag') }
}

/** Path shape for `POST …/publish` body. */
export function knowledgeBasePublishPath(filePath: string): string {
  const trimmed = filePath.trim()
  if (!trimmed) return trimmed
  return trimmed.startsWith('/')
    ? trimmed
    : `/${normalizeKnowledgeBaseFilePath(trimmed)}`
}

export function knowledgeBaseFileHasDraftDelta(
  file: FileContentResult,
): boolean {
  return (
    typeof file.draft_content === 'string' &&
    file.draft_content !== file.content
  )
}

/** Body to preview or edit: draft when it differs from published `content`. */
export function knowledgeBaseFileDisplayContent(
  file: FileContentResult,
): string {
  if (knowledgeBaseFileHasDraftDelta(file)) return file.draft_content!
  return file.content
}

export interface KnowledgeBaseFileRevisionDto {
  revision_id: string
  version_num: number
  status: 'draft' | 'published'
  /** Staging versions list: `true` = Published, `false` = Unpublished in UI. */
  published?: boolean
  is_active: boolean
  /** Real backend: current file version row. */
  is_head?: boolean
  created_at: string
  size_bytes?: number
  actions?: {
    view_version?: boolean
    publish?: boolean
    discard?: boolean
    unpublish?: boolean
    delete?: boolean
  }
}

export interface KnowledgeBaseFilePublishInput {
  revision_id?: string
  set_active?: boolean
  /** When publishing from the editor, send body text in one request (no separate PUT). */
  content?: string
}

export interface KnowledgeBaseFileRevisionActionInput {
  revision_id: string
}

export function knowledgeBaseRevisionIsPublished(
  revision: Pick<KnowledgeBaseFileRevisionDto, 'published' | 'status'>,
): boolean {
  if (typeof revision.published === 'boolean') return revision.published
  return revision.status === 'published'
}

export function knowledgeBaseRevisionStatusLabel(
  revision: Pick<KnowledgeBaseFileRevisionDto, 'published' | 'status'>,
): 'Published' | 'Unpublished' {
  return knowledgeBaseRevisionIsPublished(revision)
    ? 'Published'
    : 'Unpublished'
}

/** Version history header: Live when head row is published (`is_head` + `published`). */
export function knowledgeBaseRevisionHeadIsLive(
  revision:
    | Pick<KnowledgeBaseFileRevisionDto, 'is_head' | 'published' | 'status'>
    | null
    | undefined,
): boolean {
  if (!revision || revision.is_head !== true) return false
  return knowledgeBaseRevisionIsPublished(revision)
}

/** Version history row: Publish when unpublished draft head (`published` false, `is_head` true). */
export function knowledgeBaseRevisionShowsPublishAction(
  revision: Pick<
    KnowledgeBaseFileRevisionDto,
    'published' | 'status' | 'is_head'
  >,
): boolean {
  return (
    !knowledgeBaseRevisionIsPublished(revision) && revision.is_head === true
  )
}

function normalizeKnowledgeBaseFileRevision(
  raw: Record<string, unknown>,
): KnowledgeBaseFileRevisionDto {
  const statusRaw = raw.status
  const status: KnowledgeBaseFileRevisionDto['status'] =
    statusRaw === 'draft' || statusRaw === 'published' ? statusRaw : 'published'
  const published =
    typeof raw.published === 'boolean' ? raw.published : status === 'published'
  const versionRaw = Number(raw.version_num)
  return {
    revision_id: String(raw.revision_id ?? raw.version_id ?? raw.id ?? ''),
    version_num: Number.isFinite(versionRaw) ? versionRaw : 0,
    status,
    published,
    is_active: raw.is_active === true,
    is_head: raw.is_head === true,
    created_at: String(
      raw.created_at ?? raw.updated_at ?? new Date(0).toISOString(),
    ),
    size_bytes: typeof raw.size_bytes === 'number' ? raw.size_bytes : undefined,
  }
}

/** Normalize GET …/files/{PATH} JSON envelopes to displayable file text. */
export function parseKnowledgeBaseFileResponse(
  filePath: string,
  json: unknown,
  contentTypeHeader: string,
): FileContentResult {
  if (typeof json === 'string') {
    return { path: filePath, content: json, contentType: contentTypeHeader }
  }

  if (json && typeof json === 'object') {
    const record = json as Record<string, unknown>
    const body =
      typeof record.content === 'string'
        ? record.content
        : typeof record.text === 'string'
          ? record.text
          : typeof record.body === 'string'
            ? record.body
            : null
    const path = typeof record.path === 'string' ? record.path : filePath
    const size =
      typeof record.size_bytes === 'number'
        ? record.size_bytes
        : typeof record.size === 'number'
          ? record.size
          : undefined
    const sha256 = typeof record.sha256 === 'string' ? record.sha256 : undefined
    const draft_content =
      typeof record.draft_content === 'string'
        ? record.draft_content
        : undefined
    const version_id =
      typeof record.version_id === 'string'
        ? record.version_id
        : typeof record.revision_id === 'string'
          ? record.revision_id
          : undefined
    const versionRaw = Number(record.version_num)
    const version_num = Number.isFinite(versionRaw) ? versionRaw : undefined

    if (body !== null) {
      const ext = path.split('.').pop()?.toLowerCase()
      let contentType = contentTypeHeader
      if (ext === 'md' || ext === 'markdown') {
        contentType = 'text/markdown'
      }
      return {
        path,
        content: body,
        draft_content,
        contentType,
        size,
        sha256,
        version_id,
        version_num,
      }
    }
  }

  return {
    path: filePath,
    content: JSON.stringify(json, null, 2),
    contentType: contentTypeHeader,
  }
}

/**
 * Normalizes an API path against API_BASE (handles /v1 prefix gracefully)
 */
function kbFilePath(kbId: string, filePath: string, suffix = ''): string {
  const cleanPath = filePath.replace(/^\/+/, '')
  return normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/files/${cleanPath}${suffix}`,
  )
}

function normalizePath(endpoint: string): string {
  const clean = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  // If API_BASE already ends with /v1, strip any leading /v1
  if (API_BASE.endsWith('/v1') && clean.startsWith('/v1/')) {
    return clean.substring(3)
  }
  return clean
}

export type KnowledgeBaseListScope = 'active' | 'archived' | 'deleted'

function parseKnowledgeBaseListResponse(res: unknown): KnowledgeBaseDto[] {
  if (Array.isArray(res)) return res
  if (res && typeof res === 'object') {
    const record = res as {
      items?: KnowledgeBaseDto[]
      knowledge_bases?: KnowledgeBaseDto[]
      data?: KnowledgeBaseDto[]
    }
    return record.items ?? record.knowledge_bases ?? record.data ?? []
  }
  return []
}

/**
 * List knowledge bases for a lifecycle tab.
 * GET /knowledge_bases/active | /archived | /deleted
 */
export async function fetchKnowledgeBases(params: {
  scope: KnowledgeBaseListScope
  limit?: number
}): Promise<KnowledgeBaseDto[]> {
  const query = new URLSearchParams()
  query.set('limit', String(params.limit ?? 20))
  const path = normalizePath(
    `/knowledge_bases/${params.scope}?${query.toString()}`,
  )
  const res = await api.get<unknown>(path)
  return parseKnowledgeBaseListResponse(res)
}

/** Row for the Knowledge Graph knowledge-base picker. */
export interface PublishedKnowledgeBaseOption {
  id: string
  label: string
}

/**
 * GET /knowledge_bases/published?limit=20
 * Published bases the graph picker can load.
 */
export async function fetchPublishedKnowledgeBases(params?: {
  limit?: number
}): Promise<PublishedKnowledgeBaseOption[]> {
  const query = new URLSearchParams()
  query.set('limit', String(params?.limit ?? 20))
  const path = normalizePath(`/knowledge_bases/published?${query.toString()}`)
  const res = await api.get<unknown>(path)
  return parseKnowledgeBaseListResponse(res).flatMap((item) => {
    const id = item.id || item.kb_id || item.knowledge_base_id
    if (!id) return []
    return [
      {
        id,
        label: item.display_name || item.name || id,
      },
    ]
  })
}

export type KnowledgeGraphRef = 'published' | 'draft'

/**
 * GET /knowledge_bases/{kb_id}/graph?ref=published|draft&queryable_only=false&include_scope_edges=true
 * `ref=draft` when the open base has no `published_release_id`.
 */
export async function fetchKnowledgeBaseGraph(
  kbId: string,
  ref: KnowledgeGraphRef = 'published',
): Promise<KnowledgeGraph> {
  const query = new URLSearchParams({
    ref,
    queryable_only: 'false',
    include_scope_edges: 'true',
  })
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/graph?${query.toString()}`,
  )
  return api.get<KnowledgeGraph>(path)
}

/**
 * 2. Open that Knowledge Base with ID
 * GET /knowledge_bases/{KB_ID}
 */
export async function fetchKnowledgeBaseById(
  kbId: string,
): Promise<KnowledgeBaseDto> {
  const path = normalizePath(`/knowledge_bases/${encodeURIComponent(kbId)}`)
  try {
    const token = useAuthStore.getState().accessToken
    const headers = new Headers()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    const res = await fetch(`${API_BASE}${path}`, {
      headers,
      credentials: 'same-origin',
    })
    if (res.ok) {
      const etagHeader = res.headers.get('ETag')
      const json = (await res.json()) as KnowledgeBaseDto
      if (etagHeader && !json.etag) {
        json.etag = etagHeader.replace(/^"|"$/g, '')
      }
      return json
    }
  } catch {
    // fallback to api.get
  }
  return api.get<KnowledgeBaseDto>(path)
}

/**
 * 3. List the files in that Knowledge Base
 * GET /knowledge_bases/{KB_ID}/files?depth=0&view=basic&limit=100
 */
export async function fetchKnowledgeBaseFiles(
  kbId: string,
  params?: {
    depth?: number
    view?: string
    limit?: number
  },
): Promise<KnowledgeBaseFileDto[]> {
  const query = new URLSearchParams()
  if (params?.depth != null) query.set('depth', String(params.depth))
  if (params?.view != null) query.set('view', params.view)
  if (params?.limit != null) query.set('limit', String(params.limit))
  const qs = query.toString()
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/files${qs ? `?${qs}` : ''}`,
  )
  const res = await api.get<
    | KnowledgeBaseFileDto[]
    | {
        files?: KnowledgeBaseFileDto[]
        items?: KnowledgeBaseFileDto[]
        data?: KnowledgeBaseFileDto[]
      }
  >(path)

  if (Array.isArray(res)) return res
  if (res && typeof res === 'object') {
    return res.files ?? res.items ?? res.data ?? []
  }
  return []
}

/**
 * 4. Open that particular File
 * GET /knowledge_bases/{KB_ID}/files/{PATH}
 */
export interface FetchKnowledgeBaseFileOptions {
  version_num?: number
  /** Real backend version row id (`version_id` on the wire). */
  version_id?: string
  /** Legacy / OpenAPI name; sent only when `version_id` is omitted. */
  revision_id?: string
}

export async function fetchKnowledgeBaseFile(
  kbId: string,
  filePath: string,
  options?: FetchKnowledgeBaseFileOptions,
): Promise<FileContentResult> {
  const cleanPath = filePath.replace(/^\/+/, '')
  const query = new URLSearchParams()
  if (options?.version_num != null) {
    query.set('version_num', String(options.version_num))
  }
  const versionId = options?.version_id ?? options?.revision_id
  if (versionId) {
    query.set('version_id', versionId)
  }
  const qs = query.toString()
  const endpoint = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/files/${cleanPath}${qs ? `?${qs}` : ''}`,
  )

  const token = useAuthStore.getState().accessToken
  const headers = new Headers()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const res = await fetch(`${API_BASE}${endpoint}`, {
    headers,
    credentials: 'same-origin',
  })

  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body)
  }

  const contentType = res.headers.get('content-type') || 'text/plain'

  if (contentType.includes('application/json')) {
    const json = await res.json()
    return parseKnowledgeBaseFileResponse(filePath, json, contentType)
  }

  const text = await res.text()
  return { path: filePath, content: text, contentType }
}

/**
 * GET /knowledge_bases/{KB_ID}/versions/{VERSION_ID} — open a version in history preview.
 */
export async function fetchKnowledgeBaseFileVersion(
  kbId: string,
  versionId: string,
): Promise<FileContentResult> {
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/versions/${encodeURIComponent(versionId)}`,
  )
  const json = await api.get<unknown>(path)
  const wirePath =
    json && typeof json === 'object' && !Array.isArray(json)
      ? (json as Record<string, unknown>).path
      : undefined
  const filePath =
    typeof wirePath === 'string' && wirePath.trim() ? wirePath : versionId
  const parsed = parseKnowledgeBaseFileResponse(
    filePath,
    json,
    'application/json',
  )
  return {
    ...parsed,
    version_id: parsed.version_id ?? versionId,
  }
}

/** Files tab Eye: draft → GET file; live → GET version by `live_version_id`. */
export async function fetchKnowledgeBaseFileForPreview(
  kbId: string,
  file: KnowledgeBaseFileDto,
): Promise<FileContentResult> {
  if (knowledgeBaseFileIsLive(file)) {
    const liveVersionId = file.live_version_id?.trim()
    if (liveVersionId) {
      return fetchKnowledgeBaseFileVersion(kbId, liveVersionId)
    }
  }
  return fetchKnowledgeBaseFile(kbId, file.path)
}

/**
 * Create a new Knowledge Base
 * POST /knowledge_bases
 */
export async function createKnowledgeBaseApi(data: {
  name: string
  display_name?: string
  description?: string
  default_instructions?: string
  tags?: string[]
}): Promise<KnowledgeBaseDto> {
  const path = normalizePath('/knowledge_bases')
  return api.post<KnowledgeBaseDto>(path, data)
}

export interface PatchKnowledgeBasePayload {
  display_name?: string
  description?: string
  name?: string
  is_archived?: boolean
  is_deleted?: boolean
  deleted?: boolean
}

/**
 * PATCH /knowledge_bases/{KB_ID} — update display_name, description, or lifecycle.
 */
export async function patchKnowledgeBaseApi(
  kbId: string,
  body: PatchKnowledgeBasePayload,
  etag?: string,
): Promise<KnowledgeBaseDto> {
  const path = normalizePath(`/knowledge_bases/${encodeURIComponent(kbId)}`)
  const cleanEtag = etag?.trim().replace(/^"|"$/g, '')
  return api.patch<KnowledgeBaseDto>(path, body, {
    headers: cleanEtag ? { 'If-Match': `"${cleanEtag}"` } : undefined,
  })
}

/** POST /knowledge_bases/{KB_ID}/archive */
export async function archiveKnowledgeBaseApi(
  kbId: string,
): Promise<KnowledgeBaseDto> {
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/archive`,
  )
  return api.post<KnowledgeBaseDto>(path, {})
}

/** POST /knowledge_bases/{KB_ID}/unarchive — restore archived KB to Active */
export async function unarchiveKnowledgeBaseApi(
  kbId: string,
): Promise<KnowledgeBaseDto> {
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/unarchive`,
  )
  return api.post<KnowledgeBaseDto>(path, {})
}

/** POST /knowledge_bases/{KB_ID}/restore — restore from Trash to Active */
export async function restoreKnowledgeBaseApi(
  kbId: string,
): Promise<KnowledgeBaseDto> {
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/restore`,
  )
  return api.post<KnowledgeBaseDto>(path, {})
}

/** POST /knowledge_bases/{KB_ID}/trash — move to trash (`deleted: true`). */
export async function trashKnowledgeBaseApi(
  kbId: string,
): Promise<KnowledgeBaseDto> {
  const path = normalizePath(`/knowledge_bases/${encodeURIComponent(kbId)}`)
  return api.delete<KnowledgeBaseDto>(path)
}

/** DELETE /knowledge_bases/trash/{KB_ID} — permanent removal from Trash tab */
export async function deleteTrashedKnowledgeBaseApi(
  kbId: string,
): Promise<void> {
  const path = normalizePath(
    `/knowledge_bases/trash/${encodeURIComponent(kbId)}`,
  )
  await api.delete(path)
}

/** DELETE /knowledge_bases/{KB_ID} — legacy permanent removal. */
export async function deleteKnowledgeBaseApi(kbId: string): Promise<void> {
  const path = normalizePath(`/knowledge_bases/${encodeURIComponent(kbId)}`)
  await api.delete(path)
}

async function fetchKnowledgeBaseChangesRaw(
  kbId: string,
  paths?: string,
): Promise<{ detail: KnowledgeBaseChangesDetail; etag: string | null }> {
  const query = new URLSearchParams()
  const pathParam = paths?.trim().replace(/^\/+/, '')
  if (pathParam) query.set('paths', pathParam)
  const qs = query.toString()
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/changes${qs ? `?${qs}` : ''}`,
  )
  const { json: res, etag } = await getKnowledgeBaseChangesJson(path)
  return { detail: parseKnowledgeBaseChangesDetail(res), etag }
}

/** GET /knowledge_bases/{KB_ID}/changes — staging envelope for Changes tab. */
export async function fetchKnowledgeBaseChangesDetail(
  kbId: string,
  paths?: string,
): Promise<KnowledgeBaseChangesDetail> {
  const { detail } = await fetchKnowledgeBaseChangesRaw(kbId, paths)
  return detail
}

/**
 * GET /knowledge_bases/{KB_ID}/changes — optional `paths` scopes pending draft hash.
 */
export async function fetchKnowledgeBaseChangesSnapshot(
  kbId: string,
  paths?: string,
): Promise<KnowledgeBaseChangesSnapshot> {
  const { detail, etag } = await fetchKnowledgeBaseChangesRaw(kbId, paths)
  const if_match = knowledgeBaseChangesIfMatch(etag, detail.draft_sha256)
  return { ...detail, if_match }
}

/**
 * POST /knowledge_bases/{KB_ID}/publish — after GET …/changes
 * with `If-Match: "<draft_sha256>"` when `draft_sha256` is present.
 * Assumed contract; see specs/api/drift.md.
 */
export async function publishKnowledgeBaseApi(
  kbId: string,
  options?: KnowledgeBasePublishOptions,
): Promise<KnowledgeBaseDto> {
  const explicitPaths = options?.paths?.filter((p) => p?.trim()) ?? []
  let scopePath: string | undefined
  if (explicitPaths.length === 1) {
    scopePath = normalizeKnowledgeBaseFilePath(explicitPaths[0]!)
  } else if (options?.filePath?.trim()) {
    scopePath = normalizeKnowledgeBaseFilePath(options.filePath)
  }
  const snapshot = await fetchKnowledgeBaseChangesSnapshot(kbId, scopePath)
  if (snapshot.publishable === false) {
    throw new KnowledgeBaseNotPublishableError()
  }
  const draft_sha256 =
    snapshot.draft_sha256?.trim() || options?.draft_sha256?.trim()
  if (!draft_sha256) {
    throw new ApiError(422, {
      detail: 'draft_sha256 missing from changes response',
    })
  }
  const hash = draft_sha256.replace(/^"|"$/g, '')
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/publish`,
  )
  const ifMatch = hash ? `"${hash}"` : undefined
  const wirePath = options?.filePath?.trim()
  const bodyPaths =
    explicitPaths.length > 0
      ? explicitPaths.map((p) => knowledgeBasePublishPath(p))
      : wirePath
        ? [knowledgeBasePublishPath(wirePath)]
        : undefined
  const body = bodyPaths?.length ? { paths: bodyPaths } : {}
  return api.post<KnowledgeBaseDto>(
    path,
    body,
    ifMatch ? { headers: { 'If-Match': ifMatch } } : undefined,
  )
}

/**
 * Staging authoring flow for a file: optional PUT draft, then changes → publish.
 */
export async function publishKnowledgeBaseWithDraftSave(
  kbId: string,
  filePath?: string,
  options?: {
    content?: string
    write?: KnowledgeBaseFileWriteRequest
    sha256?: string
  },
): Promise<KnowledgeBaseDto> {
  const apiPath = filePath
    ? normalizeKnowledgeBaseFilePath(filePath)
    : undefined
  const draftPayload =
    options?.write ?? (options?.content != null ? options.content : undefined)
  let saved: WriteFileSaveResult | undefined
  if (apiPath && draftPayload != null) {
    saved = await saveKnowledgeBaseFileDraft(
      kbId,
      apiPath,
      draftPayload,
      options?.sha256,
    )
    if (saved.created) {
      return { id: kbId } as KnowledgeBaseDto
    }
  }
  return publishKnowledgeBaseApi(
    kbId,
    filePath?.trim() ? { filePath } : undefined,
  )
}

/**
 * POST /knowledge_bases/{KB_ID}/unpublish — revert KB to draft.
 * Assumed contract; see specs/api/drift.md.
 */
export async function unpublishKnowledgeBaseApi(
  kbId: string,
): Promise<KnowledgeBaseDto> {
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/unpublish`,
  )
  return api.post<KnowledgeBaseDto>(path, {})
}

/**
 * GET /knowledge_bases/{KB_ID}/versions?Path={PATH}&limit=50
 */
export async function fetchKnowledgeBaseFileRevisions(
  kbId: string,
  filePath: string,
  limit = 50,
): Promise<KnowledgeBaseFileRevisionDto[]> {
  const query = new URLSearchParams()
  query.set('path', filePath.replace(/^\/+/, ''))
  query.set('limit', String(limit))
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/versions?${query.toString()}`,
  )
  const res = await api.get<
    | KnowledgeBaseFileRevisionDto[]
    | {
        versions?: KnowledgeBaseFileRevisionDto[]
        revisions?: KnowledgeBaseFileRevisionDto[]
        items?: KnowledgeBaseFileRevisionDto[]
      }
  >(path)
  const list = Array.isArray(res)
    ? res
    : (res.versions ?? res.revisions ?? res.items ?? [])
  return list.map((row) =>
    normalizeKnowledgeBaseFileRevision(
      row as unknown as Record<string, unknown>,
    ),
  )
}

/**
 * PUT /knowledge_bases/{KB_ID}/files/{PATH} — save draft content
 */
export async function saveKnowledgeBaseFileDraft(
  kbId: string,
  filePath: string,
  payload: KnowledgeBaseFileDraftSavePayload,
  sha256?: string,
): Promise<WriteFileSaveResult> {
  const path = kbFilePath(kbId, normalizeKnowledgeBaseFilePath(filePath))
  const res = await api.put<unknown>(
    path,
    knowledgeBaseFileDraftPutBody(payload),
    {
      headers: sha256 ? { 'If-Match': `"${sha256}"` } : undefined,
    },
  )
  return parseWriteFileSaveResponse(res)
}

/**
 * POST /knowledge_bases/{KB_ID}/files/{PATH}/publish
 */
export async function publishKnowledgeBaseFile(
  kbId: string,
  filePath: string,
  input?: KnowledgeBaseFilePublishInput,
  sha256?: string,
): Promise<KnowledgeBaseFileRevisionDto[]> {
  const path = kbFilePath(kbId, filePath, '/publish')
  return api.post<KnowledgeBaseFileRevisionDto[]>(path, input ?? {}, {
    headers: sha256 ? { 'If-Match': `"${sha256}"` } : undefined,
  })
}

/**
 * POST /knowledge_bases/{KB_ID}/files/{PATH}/discard
 */
export async function discardKnowledgeBaseFileDraft(
  kbId: string,
  filePath: string,
  input?: { revision_id?: string },
): Promise<KnowledgeBaseFileRevisionDto[]> {
  const path = kbFilePath(kbId, filePath, '/discard')
  return api.post<KnowledgeBaseFileRevisionDto[]>(path, input ?? {})
}

/**
 * POST /knowledge_bases/{KB_ID}/versions/{VERSION_ID}/restore
 * Body `{ content }`; optional `If-Match: "<sha256>"` on current file head.
 */
export async function restoreKnowledgeBaseVersion(
  kbId: string,
  versionId: string,
  options: { content: string; sha256?: string },
): Promise<unknown> {
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/versions/${encodeURIComponent(versionId)}/restore`,
  )
  const hash = options.sha256?.trim().replace(/^"|"$/g, '')
  return api.post(
    path,
    { content: options.content },
    hash ? { headers: { 'If-Match': `"${hash}"` } } : undefined,
  )
}

/** Loads version content + file head sha256, then POST …/versions/{id}/restore. */
export async function restoreKnowledgeBaseFileVersion(
  kbId: string,
  filePath: string,
  versionId: string,
): Promise<unknown> {
  const apiPath = normalizeKnowledgeBaseFilePath(filePath)
  const [version, head] = await Promise.all([
    fetchKnowledgeBaseFileVersion(kbId, versionId),
    fetchKnowledgeBaseFile(kbId, apiPath),
  ])
  return restoreKnowledgeBaseVersion(kbId, versionId, {
    content: version.content ?? '',
    sha256: head.sha256,
  })
}

/**
 * POST /knowledge_bases/{KB_ID}/files/{PATH}/unpublish
 */
export async function unpublishKnowledgeBaseFileRevision(
  kbId: string,
  filePath: string,
  input: KnowledgeBaseFileRevisionActionInput,
): Promise<KnowledgeBaseFileRevisionDto[]> {
  const path = kbFilePath(kbId, filePath, '/unpublish')
  return api.post<KnowledgeBaseFileRevisionDto[]>(path, input)
}

/**
 * DELETE /knowledge_bases/{KB_ID}/files/{PATH}
 */
export async function deleteKnowledgeBaseFile(
  kbId: string,
  filePath: string,
): Promise<void> {
  const path = kbFilePath(kbId, filePath)
  await api.delete(path)
}

/** @deprecated Prefer `fetchKnowledgeBaseChangesDetail` + `knowledgeBaseChangesTableRows`. */
export async function fetchKnowledgeBaseChanges(
  kbId: string,
): Promise<KnowledgeBaseChangeDto[]> {
  const detail = await fetchKnowledgeBaseChangesDetail(kbId)
  return knowledgeBaseChangesTableRows(detail.changes).map((row) => ({
    path: row.path,
    change_type: row.kind,
  }))
}

/** GET /knowledge_bases/{KB_ID}/releases?limit=50 */
export async function fetchKnowledgeBaseReleases(
  kbId: string,
  limit = 50,
): Promise<KnowledgeBaseReleaseDto[]> {
  const page = await fetchKnowledgeBaseReleasesPage(kbId, limit)
  return page.releases
}

export async function fetchKnowledgeBaseReleasesPage(
  kbId: string,
  limit = 50,
): Promise<KnowledgeBaseReleasesPage> {
  const query = new URLSearchParams()
  query.set('limit', String(limit))
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/releases?${query.toString()}`,
  )
  const res = await api.get<unknown>(path)
  const releases = parseListEnvelope<KnowledgeBaseReleaseDto>(res, [
    'releases',
    'items',
    'data',
  ])
  const kb_id =
    res && typeof res === 'object' && !Array.isArray(res)
      ? (res as Record<string, unknown>).kb_id
      : undefined
  const next_cursor: any =
    res && typeof res === 'object' && !Array.isArray(res)
      ? (res as Record<string, unknown>).next_cursor
      : undefined
  return {
    kb_id: typeof kb_id === 'string' ? kb_id : kbId,
    releases,
    next_cursor:
      next_cursor === null || typeof next_cursor === 'string'
        ? next_cursor
        : undefined,
  }
}

/** GET /knowledge_bases/{KB_ID}/events?limit=100 */
export async function fetchKnowledgeBaseEvents(
  kbId: string,
  limit = 100,
): Promise<KnowledgeBaseEventDto[]> {
  const page = await fetchKnowledgeBaseEventsPage(kbId, limit)
  return page.events
}

export async function fetchKnowledgeBaseEventsPage(
  kbId: string,
  limit = 100,
): Promise<KnowledgeBaseEventsPage> {
  const query = new URLSearchParams()
  query.set('limit', String(limit))
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/events?${query.toString()}`,
  )
  const res = await api.get<unknown>(path)
  const events = parseListEnvelope<KnowledgeBaseEventDto>(res, [
    'events',
    'items',
    'data',
  ])
  const kb_id =
    res && typeof res === 'object' && !Array.isArray(res)
      ? (res as Record<string, unknown>).kb_id
      : undefined
  const next_cursor: any =
    res && typeof res === 'object' && !Array.isArray(res)
      ? (res as Record<string, unknown>).next_cursor
      : undefined
  return {
    kb_id: typeof kb_id === 'string' ? kb_id : kbId,
    events,
    next_cursor:
      next_cursor === null || typeof next_cursor === 'string'
        ? next_cursor
        : undefined,
  }
}

/** GET /knowledge_bases/{KB_ID}/authoring_options */
export async function fetchKnowledgeBaseAuthoringOptions(
  kbId: string,
): Promise<KnowledgeBaseAuthoringOptions> {
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/authoring_options`,
  )
  const res = await api.get<unknown>(path)
  return normalizeKnowledgeBaseAuthoringOptions(res)
}

export async function fetchKnowledgeBaseAuthoringOptionsResolved(
  kbId: string,
): Promise<KnowledgeBaseAuthoringOptionsResolved> {
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/authoring_options`,
  )
  const res = await api.get<unknown>(path)
  const options = normalizeKnowledgeBaseAuthoringOptions(res)
  return resolveKnowledgeBaseAuthoringOptions(options, res)
}

export async function fetchKnowledgeBaseAddFilePrefill(
  kbId: string,
): Promise<KnowledgeBaseAddFilePrefill> {
  const resolved = await fetchKnowledgeBaseAuthoringOptionsResolved(kbId)
  return resolved.formPrefill
}

/** POST /knowledge_bases/{KB_ID}/revert — after GET …/changes; optional `{ paths }`. */
export async function revertKnowledgeBaseDraft(
  kbId: string,
  options?: { paths?: string[] },
): Promise<KnowledgeBaseDto> {
  const explicitPaths = options?.paths?.filter((p) => p?.trim()) ?? []
  let scopePath: string | undefined
  if (explicitPaths.length === 1) {
    scopePath = normalizeKnowledgeBaseFilePath(explicitPaths[0]!)
  }
  const snapshot = await fetchKnowledgeBaseChangesSnapshot(kbId, scopePath)
  const draft_sha256 = snapshot.draft_sha256?.trim().replace(/^"|"$/g, '')
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/revert`,
  )
  const wirePaths = explicitPaths.map((p) => knowledgeBasePublishPath(p))
  const body = wirePaths.length > 0 ? { paths: wirePaths } : {}
  return api.post<KnowledgeBaseDto>(
    path,
    body,
    draft_sha256 ? { headers: { 'If-Match': `"${draft_sha256}"` } } : undefined,
  )
}

/** POST /knowledge_bases/{KB_ID}/releases/{RELEASE_ID}/rollback — after GET …/changes for live release id. */
export async function rollbackKnowledgeBaseRelease(
  kbId: string,
  releaseId: string,
): Promise<KnowledgeBaseDto> {
  const snapshot = await fetchKnowledgeBaseChangesSnapshot(kbId)
  const published_release_id = snapshot.published_release_id
    ?.trim()
    .replace(/^"|"$/g, '')
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/releases/${encodeURIComponent(releaseId)}/rollback`,
  )
  return api.post<KnowledgeBaseDto>(
    path,
    {},
    published_release_id
      ? { headers: { 'If-Match': `"${published_release_id}"` } }
      : undefined,
  )
}

/** POST /knowledge_bases/{KB_ID}/releases/{RELEASE_ID}/publish — promote draft release. */
export async function publishKnowledgeBaseRelease(
  kbId: string,
  releaseId: string,
): Promise<KnowledgeBaseDto> {
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/releases/${encodeURIComponent(releaseId)}/publish`,
  )
  return api.post<KnowledgeBaseDto>(path, {})
}

/**
 * PATCH /knowledge_bases/{KB_ID}/files/{PATH} — rename (If-Match sha256).
 */
export type KnowledgeBaseAiGenerateFileResult = {
  path: string
  name?: string
  sha256?: string
}

function parseKnowledgeBaseAiGenerateFileResult(
  res: unknown,
): KnowledgeBaseAiGenerateFileResult {
  const o =
    res && typeof res === 'object' && !Array.isArray(res)
      ? (res as Record<string, unknown>)
      : {}
  const path =
    typeof o.path === 'string'
      ? o.path
      : typeof o.file_path === 'string'
        ? o.file_path
        : ''
  if (!path.trim()) {
    throw new Error('Invalid generate response')
  }
  return {
    path,
    name: typeof o.name === 'string' ? o.name : undefined,
    sha256: typeof o.sha256 === 'string' ? o.sha256 : undefined,
  }
}

/** POST /knowledge_bases/{KB_ID}/files/generate — AI-authored file from a prompt. */
export async function generateKnowledgeBaseFileFromAi(
  kbId: string,
  prompt: string,
): Promise<KnowledgeBaseAiGenerateFileResult> {
  const trimmed = prompt.trim()
  if (!trimmed) {
    throw new Error('Prompt required')
  }
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/files/generate`,
  )
  const res = await api.post<unknown>(path, { prompt: trimmed })
  return parseKnowledgeBaseAiGenerateFileResult(res)
}

export async function renameKnowledgeBaseFile(
  kbId: string,
  filePath: string,
  newPath: string,
  sha256?: string,
): Promise<KnowledgeBaseFileDto> {
  const cleanPath = filePath.replace(/^\/+/, '')
  const path = normalizePath(
    `/knowledge_bases/${encodeURIComponent(kbId)}/files/${cleanPath}`,
  )
  return api.patch<KnowledgeBaseFileDto>(
    path,
    { path: newPath.replace(/^\/+/, '') },
    {
      headers: sha256 ? { 'If-Match': `"${sha256}"` } : undefined,
    },
  )
}
